import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../../context/AppContext';
import { REPLACES_PRICES, REPLACES_ROW_INFO } from './hubReplacesData';

// "What this replaces" on the Hub card, for the sales page (HubVsAgents) and
// /pricing (PricingPage). The same as sales-page-next/components/HubReplaces.tsx,
// except that the React app has one currency at a time (useApp().currency), so only
// that currency's amounts are rendered. Inline styles only, so the sizes are the
// same on a phone and on desktop, and on both pages. Each card passes its own
// currency table, so the symbol matches the card's own price.

// The captions use the Next site's darkened muted blue (it clears the 4.5:1
// contrast on white). The React --sp-text-muted is the lighter #6D89AF, so the
// colour is written out here.
const CAPTION = '#5978A3';
const MUTED = '#94a3b8';

// Same thousands rule as the Next formatMoney: a comma once the whole amount is
// longer than 3 digits (only the SEK total gets there). Symbol and suffix come
// from the card's own PRICING table.
function formatMoney(c, amount) {
   const digits = String(amount);
   const grouped = digits.length <= 3 ? digits : `${digits.slice(0, digits.length - 3)},${digits.slice(digits.length - 3)}`;
   return c.suffix ? `${grouped}${c.symbol}` : `${c.symbol}${grouped}`;
}

const PRICE_KEY = { plain: 'price', courses: 'priceCourses', communities: 'priceCommunities' };

// The card's currency, falling back to USD like the card's own price does.
function useReplacesCurrency(currencies) {
   const { currency } = useApp();
   const code = currencies[currency] && REPLACES_PRICES[currency] ? currency : 'USD';
   return { cur: currencies[code], prices: REPLACES_PRICES[code] };
}

// Under a feature label: "Replaces", the tool's logo(s), then its price.
export function ReplacesLine({ row, billing, currencies }) {
   const { t } = useTranslation();
   const { cur, prices } = useReplacesCurrency(currencies);
   const { logos, kind } = REPLACES_ROW_INFO[row];
   const term = billing === 'annually' ? 0 : 1;
   const amount = formatMoney(cur, prices.rows[row][term]);
   return (
      <span style={{ display: 'block', marginTop: 4, fontSize: 12, fontWeight: 500, lineHeight: 1.5, color: CAPTION, textTransform: 'none' }}>
         <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 7px' }}>
            <span>{t('hubReplaces.replaces')}</span>
            {logos.map(logo => (
               <span key={logo.alt} style={{ display: 'flex' }}>
                  <img
                     src={logo.src}
                     alt={logo.alt}
                     width={logo.width}
                     height={logo.height}
                     loading="lazy"
                     decoding="async"
                     style={{ height: logo.show, width: 'auto', flexShrink: 0 }}
                  />
               </span>
            ))}
         </span>
         <span style={{ display: 'block', color: MUTED }}>
            {t(`hubReplaces.${PRICE_KEY[kind]}${prices.converted ? 'About' : ''}`, { amount })}
         </span>
      </span>
   );
}

// Under the card's price: the same six jobs bought as separate tools, struck
// through. It follows the Monthly/Annually toggle.
export function ReplacesTotal({ billing, currencies }) {
   const { t } = useTranslation();
   const { cur, prices } = useReplacesCurrency(currencies);
   const term = billing === 'annually' ? 0 : 1;
   const amount = formatMoney(cur, prices.total[term]);
   return (
      <p style={{ margin: '10px 0 0', fontSize: 14, fontWeight: 400, lineHeight: 1.45, color: CAPTION, textTransform: 'none' }}>
         <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', margin: -1, padding: 0, border: 0 }}>{t('hubReplaces.totalSr')}</span>
         <s style={{ color: '#ef4444', fontWeight: 700 }}>{t('hubReplaces.price', { amount })}</s>
         {' '}
         {t('hubReplaces.total')}
      </p>
   );
}
